import type { RunState } from "../domain/types/index.js";
import { toResultPayload, type RunOutcome } from "./playLogResult.js";

const SESSION_STORAGE_KEY = "management-roguelike:analytics-session:v1";
const GAME_VERSION = import.meta.env.VITE_GAME_VERSION ?? "dev";

type SessionResponse = { token: string };

export type StageProgress = {
  stageId: string;
  cleared: boolean;
  averageAttemptsToFirstClear: number | null;
  averageClearRate: number | null;
  clearRunCount: number;
  completedRunCount: number;
};

export type NodeEventType = "node_entered" | "node_cleared" | "node_dropped";

type RunPayload = {
  id: string;
  stageId: string;
  seed: number;
  gameVersion: string;
  bringInCardId: string | null;
};

let sessionPromise: Promise<string | null> | null = null;

function readSessionToken(): string | null {
  try {
    return localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function saveSessionToken(token: string): void {
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, token);
  } catch {
    // プレイ記録に失敗してもゲーム本体は続行する。
  }
}

async function createSession(): Promise<string | null> {
  try {
    const response = await fetch("/api/anonymous-sessions", { method: "POST" });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    if (!isSessionResponse(body)) return null;
    saveSessionToken(body.token);
    return body.token;
  } catch {
    return null;
  }
}

function getSessionToken(): Promise<string | null> {
  const existing = readSessionToken();
  if (existing) return Promise.resolve(existing);
  sessionPromise ??= createSession().finally(() => { sessionPromise = null; });
  return sessionPromise;
}

async function request(path: string, init: RequestInit): Promise<void> {
  const token = await getSessionToken();
  if (!token) return;
  try {
    await fetch(path, {
      ...init,
      headers: {
        ...init.headers,
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
  } catch {
    // ネットワーク断中でもローカルのゲーム進行を妨げない。
  }
}

function isSessionResponse(value: unknown): value is SessionResponse {
  return typeof value === "object" && value !== null
    && "token" in value && typeof value.token === "string";
}

function isStageProgressResponse(value: unknown): value is { stages: StageProgress[] } {
  return typeof value === "object" && value !== null && "stages" in value
    && Array.isArray(value.stages)
    && value.stages.every((stage) => typeof stage === "object" && stage !== null
      && "stageId" in stage && typeof stage.stageId === "string"
      && "cleared" in stage && typeof stage.cleared === "boolean"
      && "averageAttemptsToFirstClear" in stage
      && (typeof stage.averageAttemptsToFirstClear === "number" || stage.averageAttemptsToFirstClear === null)
      && "averageClearRate" in stage
      && (typeof stage.averageClearRate === "number" || stage.averageClearRate === null)
      && "clearRunCount" in stage && typeof stage.clearRunCount === "number"
      && "completedRunCount" in stage && typeof stage.completedRunCount === "number");
}

export function toRunPayload(run: RunState): RunPayload {
  return {
    id: run.runId,
    stageId: run.stageId,
    seed: run.initialSeed ?? run.rngState,
    gameVersion: GAME_VERSION,
    bringInCardId: run.bringInCardId ?? null,
  };
}

export function recordRunStarted(run: RunState): Promise<void> {
  return request("/api/runs", { method: "POST", body: JSON.stringify(toRunPayload(run)) });
}

export function recordRunFinished(run: RunState, outcome: RunOutcome): Promise<void> {
  return request(`/api/runs/${encodeURIComponent(run.runId)}/result`, {
    method: "PUT",
    body: JSON.stringify(toResultPayload(run, outcome)),
  });
}

export function recordNodeEvent(runId: string, eventType: NodeEventType, nodeId: string): Promise<void> {
  return request(`/api/runs/${encodeURIComponent(runId)}/events`, {
    method: "POST",
    body: JSON.stringify({ id: crypto.randomUUID(), eventType, nodeId }),
  });
}

export async function loadStageProgress(): Promise<StageProgress[]> {
  const token = await getSessionToken();
  if (!token) return [];
  try {
    const response = await fetch("/api/stage-progress", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return [];
    const body: unknown = await response.json();
    return isStageProgressResponse(body) ? body.stages : [];
  } catch {
    return [];
  }
}
